import { z } from 'zod';
import { CUSTOM_FIELD_TEXT_VALUE_MAX_LENGTH } from './custom-field';
import { ulidSchema } from './id';

/**
 * The board filter — the contract layer for block **B19**.
 *
 * `BE-19.1` (`FR-SEARCH-008` compose filters as OR within a facet and AND
 * across facets, `FR-SEARCH-002` filter by member including cards with no
 * member, `FR-SEARCH-003` filter by label including cards with no label).
 *
 * ---------------------------------------------------------------------------
 * WHAT A FILTER IS, WHICH IS THE THING THAT DECIDES EVERY SHAPE BELOW
 * ---------------------------------------------------------------------------
 * `FR-SEARCH-002`: *"A filter hides non-matching cards in place without moving
 * or modifying them."* Three consequences, and they are the reason this file
 * declares **no view at all**:
 *
 *   1. **A filter is not a search.** It never leaves the board, never ranks,
 *      never returns a card from somewhere else, and produces no result list of
 *      its own. Cross-board search is the `FR-SEARCH-014`–`FR-SEARCH-018`
 *      family (**B20**, and the four-source text half **B33**) — a different
 *      requirement group with a different owner. ⚠ **This cited
 *      `FR-SEARCH-011` and is corrected in place**: `FR-SEARCH-011` is *"Persist
 *      a board's filter across sessions"*, which is not search at all — it is
 *      this very facet set, stored, and it is `BE-32.2`'s. ⚠ **`BE-32.2` has
 *      since landed**: `boardFilterStateSchema`, declared further down, is
 *      its wire contract, and `apps/server/src/routes/board-filter-state.ts`
 *      is where it is actually read and written. Point 3 below still holds
 *      for `board-contents`'s own response — that shape gains nothing from
 *      either block; the new response lives on a different route entirely.
 *   2. **A filter writes nothing.** It is private view state belonging to the
 *      caller who typed it — `FR-SEARCH-001` AC4 has the board update as soon
 *      as a criterion is picked, with no Apply step — so there is no filter
 *      row, no saved filter, no event and no notification. ⚠ **AC4 is only
 *      half the authority and the other half was mis-stated here**: it says
 *      there is no *Apply* step, not that nothing is stored. What makes this
 *      block store nothing is that storing it is a **different requirement** —
 *      `FR-SEARCH-011`, per board, per user, per browser, `BE-32.2` — and
 *      `FR-SEARCH-012`'s shareable filter URL is `FE-32.2`'s and is still just
 *      a query string over these same two keys.
 *   3. **A filter therefore has no response shape.** It narrows an existing
 *      one. `boardContentsSchema` in `./board-contents` is what
 *      `GET /boards/:id/contents` answers with, filtered or not, and this file
 *      adds not one field to it.
 *
 * So everything here is **input**, and the input/view division every other
 * feature file in this package opens with is stated only to record that this
 * one is all input — the mirror image of `./board-contents`, which is all view.
 *
 * ---------------------------------------------------------------------------
 * ⚠ QUERY PARAMETERS ON A SHIPPED ROUTE, NOT A ROUTE OF ITS OWN
 * ---------------------------------------------------------------------------
 * `boardFilterQuerySchema` **is** the querystring schema of B13's
 * `GET /boards/:id/contents`. There is no `POST /boards/:id/filter`, no
 * `/boards/:id/filtered-contents` and no second handler anywhere.
 *
 * A filter is a *parameter of the board face read*, not a new subject: it
 * answers the same question B13 answers ("what is on this board") with one
 * clause added, returns the identical shape, and is read by the identical
 * client screen. A second route would be a second copy of five statements, two
 * grouping loops and four mappers, kept in step with the first by hand — and
 * the copy that drifts is always the one with the extra clause. (Fastify's
 * radix router settles the question anyway: two handlers cannot mount on one
 * path.)
 *
 * ⚠ **This file is therefore load-bearing for something B13 argued at length
 * and that has nothing to do with filtering.** That route's querystring was
 * `z.strictObject({})` — an empty *strict* object whose whole job was to make
 * `?state=archived` a **422** rather than a silently ignored parameter, because
 * a client that guessed at a parameter must be told it guessed or it will read
 * an unfiltered board face as an empty archive. `boardFilterQuerySchema` is
 * `z.strictObject` for exactly that reason and must stay one: widened to
 * `z.object`, `?state=archived` starts returning 200 with the whole board and
 * B13's guarantee is gone with no test failing that was not already failing.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THE COMPOSITION RULE: OR WITHIN A FACET, AND ACROSS FACETS
 * ---------------------------------------------------------------------------
 * `FR-SEARCH-008` in one sentence: *"Within a single facet, selected options
 * are combined with OR. Across facets, a card must satisfy at least one
 * selected option in every facet where a selection was made. Facets with no
 * selection impose no constraint."*
 *
 * Read as a lattice rather than as two rules, because the two directions are
 * what its edge case says a UI must not conflate: **adding an option to a facet
 * that already has one widens the result; adding a criterion in a facet that
 * had none narrows it.** `?members=A&members=B` shows more cards than
 * `?members=A`; `?members=A&labels=X` shows fewer.
 *
 * An **absent** facet is not an empty selection — it is no constraint at all,
 * which is why both fields are `.optional()` and why the server emits no SQL
 * for a facet nobody selected in. `?members=` is **not** the way to say "no
 * constraint": it parses as the single empty string, which is neither a ULID
 * nor the sentinel, so it is a 422. Omit the key.
 *
 * ⚠ **The stricter "match all" mode was read and declined.**
 * `FR-SEARCH-008`'s behaviour text ends: *"A stricter 'match all' mode
 * requiring a card to satisfy every selected option appears in current Trello
 * documentation — verify its exact label and scope before implementing; the
 * default is the any-within-facet behaviour described here."* The verification
 * it asks for has not been done, so the mode is not built and this contract
 * carries **no mode parameter** — not even one defaulting to `any`, which would
 * be a wire commitment to a shape whose other value nobody has specified. A
 * later block that does the verification adds the parameter then; until then a
 * caller sending `?mode=all` gets a 422 from the strict object, which is the
 * honest answer.
 *
 * ---------------------------------------------------------------------------
 * ⚠ AN UNKNOWN ID IS ZERO MATCHES, NEVER AN ERROR — AND THE TWO REQUIREMENTS
 * THAT LOOK LIKE THEY DISAGREE DO NOT
 * ---------------------------------------------------------------------------
 * Nothing here, and nothing in the server, checks that a member id names a
 * member of the board or that a label id names one of its labels. A
 * well-formed ULID that matches nothing simply matches nothing.
 *
 * That is mandated, not lenient. `FR-SEARCH-002`'s edge case: *"A member
 * removed from the board while a filter on them is active: the chip remains and
 * **the board shows zero matches** rather than silently clearing."* A handler
 * that 404s an unrecognised member id makes that state unreachable — the client
 * cannot render a board with zero matches if the request it must send is
 * refused.
 *
 * ⚠ `FR-SEARCH-003`'s edge case reads the other way for labels — *"Deleting a
 * label while a filter on it is active **drops that criterion** and updates the
 * chips"* — and the asymmetry is real but it is **entirely client-side**. Both
 * sentences describe what the chip row does; the server's answer is identical
 * in both cases (an id it does not recognise contributes an `EXISTS` that no
 * card satisfies), and the difference is whether `FE-19.3` keeps the chip or
 * removes it. Reading the asymmetry as a missing server branch is the mistake
 * this paragraph exists to prevent: there is no route that could tell the two
 * apart, because a deleted label and a label on another board are the same
 * unmatched ULID here.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE DELIBERATELY DOES NOT DECLARE
 * ---------------------------------------------------------------------------
 *   - **Two of the four remaining facets — until `BE-32.1` landed too.** ⚠
 *     **This bullet has now been corrected in place TWICE, and names none
 *     absent.** It first named all four of `FR-SEARCH-004`–`FR-SEARCH-007`
 *     as absent; block **B24**'s `BE-24.5` landed two of them, and the
 *     bullet was rewritten to name the other two — `FR-SEARCH-006`
 *     (activity age) and `FR-SEARCH-007` (keyword text) — as still absent
 *     and `BE-32.1`'s, needing "either a column no shipped block writes … or
 *     a text-matching decision this file has no authority to make." **Both
 *     predictions were wrong once `BE-32.1` actually did the work**:
 *     activity age needed no new column at all — `apps/server/src/boards/
 *     filter.ts` derives "last activity" from `activity_entries`, which
 *     already existed, rather than from a stored timestamp on `cards` (see
 *     that module's own argument) — and the text-matching decision (AND-
 *     across-words, not OR) was made in `parseKeywordTerms`'s docstring,
 *     further down, rather than deferred again. `FR-SEARCH-004` and
 *     `FR-SEARCH-005` are declared below, `BE-24.5`'s — see "B24 — THE
 *     DUE-DATE BUCKET AND COMPLETION FACETS" further down. `FR-SEARCH-006`
 *     and `FR-SEARCH-007` are declared below too, `BE-32.1`'s — see
 *     "B32 — THE ACTIVITY AGE AND KEYWORD TEXT FACETS", further down still.
 *     This bullet's history is kept rather than deleted, on this block's own
 *     standing rule that an argument for why something waited stays true
 *     history once it has landed.
 *   - **A cap on how many ids a facet may carry.** No `.max()`, deliberately.
 *     `CLAUDE.md` records that the whole plan/limit corpus (`FR-PLAN-*`) is
 *     **absent from the requirements on disk**, so any number chosen here would
 *     be invented rather than sourced, and the invented number becomes a 422 on
 *     a board with more members than somebody guessed. The server dedupes
 *     instead, which is the real defence: a repeated id costs nothing in an
 *     `IN` list.
 *   - **A saved or named filter.** ⚠ **This read "`FR-SEARCH-010` is a URL" and
 *     is corrected in place: `FR-SEARCH-010` is *"Clear all filters in one
 *     action"*.** The URL is `FR-SEARCH-012` (`FE-32.2`), the per-user
 *     persistence is `FR-SEARCH-011` (`BE-32.2`), and a *named* saved filter is
 *     `FR-VIEW-*`/`B55`'s saved views. ⚠ **"Nothing is persisted here" is now
 *     corrected in place a second time: `BE-32.2` has landed, and this file
 *     declares `boardFilterStateSchema` — its JSON wire contract** — but the
 *     persistence itself (the transaction, the upsert, the label/member
 *     normalisation against the live board) is
 *     `apps/server/src/routes/board-filter-state.ts`'s, not this file's; this
 *     file still writes nothing and opens no transaction. What this bullet
 *     exists to say is still true regardless: `FR-SEARCH-010` is not the URL
 *     and never was.
 *   - **A response shape of any kind, for `GET /boards/:id/contents`.** ⚠
 *     **Narrowed in place**: this used to mean the file, full stop — see the
 *     header's point 3, which still holds for `board-contents`'s own
 *     response; this file adds not one field to `boardContentsSchema`.
 *     `boardFilterStateSchema` is the one exception, and it is a response
 *     shape for a DIFFERENT route (`GET`/`PUT /boards/:id/filter`,
 *     `BE-32.2`) that has nothing to do with narrowing `board-contents`.
 */

// ---------------------------------------------------------------------------
// Inputs — what the route ACCEPTS. There is no view half; see the header.
// ---------------------------------------------------------------------------

/**
 * The "cards with **no** member" / "cards with **no** label" option, as it is
 * spelled on the wire.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ IT IS A MEMBER OF THE FACET ARRAY, NOT A FLAG BESIDE IT
 * ---------------------------------------------------------------------------
 * The shape a reader expects is `?members=<id>&noMembers=true` — a boolean
 * parameter of its own. It is wrong, and `FR-SEARCH-002`'s edge case says why
 * in one sentence: *"'No members' combined with a named member within the same
 * facet behaves as **OR**, yielding unassigned cards **plus** that member's
 * cards."*
 *
 * A separate boolean makes that combination a special case the handler has to
 * assemble — and the natural assembly is a conjunct, because every other
 * parameter in this server narrows. `?members=<priya>&noMembers=true` would
 * then answer with the cards that are Priya's *and* have no members, which is
 * always empty: a 200, a valid body, an empty board, and nothing anywhere to
 * say the request meant the opposite. Putting the sentinel **inside** the array
 * makes the OR structural — it is one more disjunct in the facet's arm, built
 * by the same code path as an id — so the wrong answer is not expressible.
 *
 * Both requirements also describe it as an option in the facet's list rather
 * than as a control beside it: `FR-SEARCH-002`, *"The member facet lists every
 * member of the board **plus a 'No members' option**"*; `FR-SEARCH-003`, *"plus
 * a 'No labels' option"*. The wire follows the panel.
 *
 * ⚠ **The literal cannot collide with an id, and that is measurable rather than
 * hopeful.** A ULID is 26 characters of uppercase Crockford base32
 * (`ulidSchema`, `./id`); `'none'` is four lowercase characters. No id parses as
 * the sentinel and the sentinel parses as no id, in either direction, for any
 * input. A future facet whose options are not ULIDs — a due-date bucket, say —
 * must check that property again before reusing this constant rather than
 * assuming it.
 */
export const NO_FACET_MATCH = 'none';

/**
 * One selectable option in either facet: the sentinel above, or the ULID of a
 * user or a label.
 *
 * ⚠ **The union is ordered sentinel-first on purpose.** Zod reports the *last*
 * branch's issue for a value matching neither, so `?members=xyz` reports the
 * ULID format failure rather than "expected 'none'" — which is the more useful
 * message for the overwhelmingly commoner mistake. Measured, not assumed:
 * `?members=` yields the empty string and reports `invalid_format` on
 * `members.0`.
 *
 * There is no per-facet type here — a member id and a label id are both plain
 * ULIDs to the wire, and the two facets differ only in which table the server
 * looks them up in. Declaring `memberFacetOption` and `labelFacetOption` as
 * separate aliases of the same union would suggest a distinction the parser
 * cannot enforce.
 */
const boardFilterOptionSchema = z.union([z.literal(NO_FACET_MATCH), ulidSchema]);

/**
 * One facet's selection, normalised to an array.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THE `z.preprocess` IS NOT DEFENSIVE PROGRAMMING — IT IS THE COMMONEST
 * CALL. MEASURED AGAINST THIS WORKSPACE'S FASTIFY, NOT ASSUMED
 * ---------------------------------------------------------------------------
 * Fastify's default query parser collapses a repeated key and does **not**
 * normalise a single occurrence into an array. Probed in this worktree, all
 * four shapes:
 *
 * ```
 *   ?members=A              →  'A'          (a string)
 *   ?members=A&members=B    →  ['A','B']    (an array)
 *   ?members=               →  ''           (the empty string)
 *   (key absent)            →  key absent
 * ```
 *
 * So a bare `z.array(...)` here would **422 the single-selection case** — one
 * member picked in the panel, which is the most frequent request this route
 * will ever serve — while passing every test written with two. The preprocess
 * wraps a lone value before the array parse sees it, and the two-value case
 * arrives already wrapped and is untouched.
 *
 * The remaining two shapes need no special handling and get none. **An absent
 * key** never reaches the preprocess at all: `.optional()` on
 * `boardFilterQuerySchema`'s fields short-circuits `undefined` before the inner
 * pipe runs, which is what keeps "no selection" distinct from "an empty
 * selection". **`?members=`** wraps to `['']`, and the empty string is neither
 * a ULID nor the sentinel, so it is a **422** — the intended answer, not an
 * accident: a client meaning "no constraint" omits the key, and one that sent
 * an empty value has a bug worth being told about.
 *
 * ⚠ `.min(1)` is unreachable through Fastify's parser (the preprocess always
 * produces at least one element) and is kept as the contract statement for the
 * *other* consumer of this schema — `apps/web`, which imports `BoardFilterQuery`
 * to build the query string and can construct `{ members: [] }` in TypeScript.
 * An empty array is a facet that should have been omitted.
 *
 * ⚠ **`z.preprocess` compiles to a `ZodPipe`, and the OpenAPI document renders
 * the pipe's OUTPUT** — `{ type: 'array', minItems: 1, items: … }` — so `/docs`
 * describes the repeated-key form and not the single-value one it also accepts.
 * Verified against `jsonSchemaTransform` rather than assumed (the transform
 * handles the pipe without throwing, which was the risk worth measuring before
 * committing to this shape). Documenting the looser half is `FE-19.x`'s
 * problem and not a reason to make the schema stricter than the parser.
 */
export const boardFilterFacetSchema = z.preprocess(
  /**
   * ⚠ The `as unknown[]` is not a shortcut: `Array.isArray` narrows an
   * `unknown` to **`any[]`**, and returning that from a preprocess would put an
   * `any` on the boundary of the one package both halves of the product import
   * (type-aware ESLint refuses it, correctly). Re-widening to `unknown[]` costs
   * nothing — the elements are about to be parsed one by one by
   * `boardFilterOptionSchema`, which is the only thing that decides what they
   * are.
   */
  (value: unknown): unknown[] => (Array.isArray(value) ? (value as unknown[]) : [value]),
  z.array(boardFilterOptionSchema).min(1),
);

// ---------------------------------------------------------------------------
// B24 — THE DUE-DATE BUCKET AND COMPLETION FACETS (`BE-24.5`)
// ---------------------------------------------------------------------------
//
// `FR-SEARCH-004` (due-date bucket) and `FR-SEARCH-005` (completion status),
// composing with B19's OR-within/AND-across rule exactly as `members`/`labels`
// already do. Two differences from those two, both because neither new facet
// has an id to look up:
//
//   1. **No sentinel, no `NO_FACET_MATCH`.** `members`/`labels` need a literal
//      beside the ids to say "and also the ones with none"; here the "none"
//      case is already an ordinary option — `no_due_date` for the due-bucket
//      facet, and completion has no id-shaped option to need a sentinel for
//      in the first place. `NO_FACET_MATCH` stays exactly what it was: the
//      member/label facets' own literal, not a general-purpose one.
//   2. **A closed, small vocabulary rather than a ULID.** `./board-filter.ts`'s
//      own header (above) names this: "a future facet whose options are not
//      ULIDs … must check that property again before reusing this constant
//      rather than assuming it" — so both facets below get their own
//      `z.literal` union and their own preprocess pipe, never
//      `boardFilterOptionSchema`.
//
// `FR-SEARCH-008`'s composition rule is inherited for free: `matchesBoardFilter`
// in `apps/server/src/boards/filter.ts` ANDs every facet's own OR-group
// together, and adding a fifth and sixth arm to that AND is the whole of what
// wiring a new facet costs once its own OR-group is right.

/**
 * The due-date bucket facet's fixed vocabulary — `FR-SEARCH-004`: "The due
 * date facet offers relative buckets rather than absolute dates: no due
 * date, overdue, due in the next day, due in the next week, due in the next
 * month." Five named, OR-combined windows rather than an open date range —
 * "Selecting multiple buckets matches cards falling in any of them."
 *
 * ⚠ **These are not mutually exclusive partitions.** `db/postgres/migrations/
 * 0005_views_and_search.sql`'s `board_filter_states` stores the identical
 * five names as five independent `boolean` columns (`bucket_no_due_date`,
 * `bucket_overdue`, `bucket_due_next_day`, `bucket_due_next_week`,
 * `bucket_due_next_month`) rather than one enum column — a "repeated-value
 * column set", the migration's own phrase — which is the schema's own
 * argument that a card may fall in more than one bucket at once (a card due
 * in six hours is `due_next_day` **and** `due_next_week` **and**
 * `due_next_month`, all three, simultaneously) and that the caller may
 * select more than one. `apps/server/src/boards/filter.ts`'s
 * `matchesDueBucketFacet` is where the five windows are built; this file
 * only names them.
 */
export const DUE_BUCKETS = [
  'no_due_date',
  'overdue',
  'due_next_day',
  'due_next_week',
  'due_next_month',
] as const;

export const dueBucketSchema = z.literal(DUE_BUCKETS);
export type DueBucket = z.infer<typeof dueBucketSchema>;

/**
 * The window width, in whole days, that `due_next_day` / `due_next_week` /
 * `due_next_month` each open — a ROLLING window starting at the instant the
 * request is served, `[now, now + N days)`, never a calendar-day boundary.
 * `apps/server/src/boards/filter.ts`'s `matchesDueBucketFacet` is the sole
 * reader.
 *
 * `FR-SEARCH-004` names the three buckets ("day", "week", "month") and not
 * their widths in days; 1, 7 and 30 are this block's own reading of that
 * prose, on `CLAUDE.md`'s standing rule against inventing a number silently —
 * naming it here, once, is what lets a test assert the exact boundary a
 * compiled query used rather than hard-code a second copy of it.
 *
 * ⚠ **A rolling window is why `FR-SEARCH-004`'s edge case — "Buckets are
 * evaluated against the viewer's current local time" — needs no per-viewer
 * timezone work anywhere in this product.** `[now, now + N days)` denotes the
 * identical set of *instants* no matter which zone `now` is rendered in; only
 * a fixed CALENDAR boundary ("due today", "due before midnight Friday") could
 * be moved by a viewer's timezone, and none of the five buckets is
 * calendar-aligned. Do not read the edge case as unbuilt timezone work — it
 * is a property this shape already has, for free, by not being a calendar
 * boundary in the first place.
 */
export const DUE_BUCKET_WINDOW_DAYS = {
  due_next_day: 1,
  due_next_week: 7,
  due_next_month: 30,
} as const;

/**
 * One selected due-bucket option, or several — `boardFilterFacetSchema`'s
 * shape, reused as a SEPARATE preprocess pipe rather than the same schema
 * instance, because this facet's options are a five-member enum rather than
 * ULIDs (see the header's warning on exactly this point). No sentinel: unlike
 * `members`/`labels`, "nothing selected" is spelled by omitting the key
 * (`.optional()` on `boardFilterQuerySchema`, below), and `no_due_date` is
 * already an ordinary member of the enum rather than a literal beside it.
 */
const dueBucketFacetSchema = z.preprocess(
  (value: unknown): unknown[] => (Array.isArray(value) ? (value as unknown[]) : [value]),
  z.array(dueBucketSchema).min(1),
);

/**
 * The completion-status facet's fixed vocabulary — `FR-SEARCH-005`: "The card
 * status facet offers 'Marked as complete' and 'Not marked as complete' …
 * Selecting neither leaves completion state ignored."
 *
 * Two values, matching `board_filter_states.completion_filter`'s CHECK
 * constraint (`'any' | 'marked_complete' | 'not_marked_complete'`) minus its
 * `'any'` member: `'any'` is that STORED column's own "nothing selected"
 * spelling, needed because a persisted row must hold *some* value in every
 * column; this wire facet spells "nothing selected" the way `members`,
 * `labels` and `due` all do, by omitting the key, so there is no `'any'`
 * literal on the wire at all.
 */
export const COMPLETION_STATES = ['marked_complete', 'not_marked_complete'] as const;

export const completionStateSchema = z.literal(COMPLETION_STATES);
export type CompletionState = z.infer<typeof completionStateSchema>;

/**
 * One selected completion option, or both — `dueBucketFacetSchema`'s twin.
 * `FR-SEARCH-005` AC3: "Given both status options are selected, then
 * completion state stops narrowing the result and every card passes this
 * facet" — `apps/server/src/boards/filter.ts`'s `matchesCompletionFacet`
 * shows this falls out of the ordinary OR-of-two-arms machinery with no
 * special case, because `cards.completed` is `NOT NULL` and
 * `completed = true OR completed = false` is therefore satisfied by every
 * row.
 */
const completionFacetSchema = z.preprocess(
  (value: unknown): unknown[] => (Array.isArray(value) ? (value as unknown[]) : [value]),
  z.array(completionStateSchema).min(1),
);

// ---------------------------------------------------------------------------
// B32 — THE ACTIVITY AGE AND KEYWORD TEXT FACETS (`BE-32.1`)
// ---------------------------------------------------------------------------
//
// `FR-SEARCH-006` (activity age) and `FR-SEARCH-007` (keyword text) — the
// last two of the six facets `FR-SEARCH-002`–`FR-SEARCH-007` describe, and
// the pair the file's own header named as absent through two prior blocks
// (see "WHAT THIS FILE DELIBERATELY DOES NOT DECLARE", above, now corrected).
// Both differ from every facet declared so far, and in the SAME direction:
//
//   1. **Neither is a small closed vocabulary a caller may pick several
//      from.** `activity` is a five-member CHECK-constrained enum but
//      SINGLE-VALUED — `ACTIVITY_BUCKETS`'s own docstring is the argument.
//      `keyword` is not an enum at all; it is free text, tokenised by
//      `parseKeywordTerms` rather than validated against a fixed list.
//   2. **Neither gets `boardFilterFacetSchema` or a `z.preprocess` array
//      pipe.** Both are declared as plain scalars on `boardFilterQuerySchema`
//      below, so a REPEATED key (`?activity=A&activity=B`,
//      `?keyword=a&keyword=b`) arrives as an array from Fastify's parser and
//      is refused with a 422 — the correct answer for a facet this schema
//      cannot express as a selection of several, not a regression relative
//      to the four facets above.
//
// Composing: `FR-SEARCH-008`'s rule needs no seventh statement.
// `apps/server/src/boards/filter.ts`'s `matchesBoardFilter` ANDs
// `matchesActivityFacet` and `matchesKeywordFacet` into the same `and(...)`
// every other facet already builds, inheriting AND-across-facets for free.

/**
 * The activity-age facet's fixed vocabulary — `FR-SEARCH-006`: "The activity
 * facet offers relative buckets based on the timestamp of the card's most
 * recent activity … the documented shape is 'active in the last week / two
 * weeks / four weeks' plus an inverse 'without activity in the last four
 * weeks'." The requirement's own text asks the implementer to "verify the
 * exact bucket set against the shipped filter panel" — there is no shipped
 * panel, so `ck_board_filter_states_activity_bucket`
 * (`db/postgres/migrations/0005_views_and_search.sql`) answers instead:
 * these four names, in this exact spelling, are the live CHECK constraint's
 * members minus `'any'`. The schema was the authority the requirement asked
 * for, and it agrees with the prose exactly — the reverse of this
 * repository's usual direction (`CLAUDE.md`: the spec does not describe the
 * files on disk), and worth recording as such.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ SINGLE-VALUED, NOT AN ARRAY — UNLIKE `members`/`labels`/`due`/
 * `completion`, AND THE COLUMN SHAPE IS THE ARGUMENT
 * ---------------------------------------------------------------------------
 * `due` is five independent `boolean` columns on `board_filter_states` — a
 * "repeated-value column set", `DUE_BUCKETS`'s own docstring's phrase for
 * that migration's shape, and the schema's own argument that a caller may
 * select several buckets at once. `activity_bucket` is ONE `varchar(32)`
 * column under ONE CHECK constraint. The identical reading that made `due`
 * an array makes THIS a scalar: the column shape is not incidental, it is
 * the schema's own answer to "how many may a caller pick".
 *
 * `completion_filter` — also a single column — already lives this rule:
 * `COMPLETION_STATES`'s own docstring records `'any'` as "that STORED
 * column's own 'nothing selected' spelling", never spelled on the wire.
 * This facet inherits that sentence exactly, unchanged.
 *
 * The vocabulary is also SELF-CONTRADICTORY if arrayed, which is the
 * confirming case rather than the founding one: `active_last_4_weeks` and
 * `no_activity_last_4_weeks` are exact complements over the same instant, so
 * `?activity=active_last_4_weeks&activity=no_activity_last_4_weeks` would be
 * a well-formed request denoting the EMPTY set — no card is both active and
 * inactive over the identical 28 days. A scalar cannot even construct that
 * request; an array would accept it and then have nothing left to return.
 *
 * `'any'` never appears on the wire, for the reason every other facet in
 * this file already gives: "nothing selected" is spelled by OMITTING the
 * key (`.optional()` on `boardFilterQuerySchema`, below), never by sending
 * the stored column's own placeholder value.
 */
export const ACTIVITY_BUCKETS = [
  'active_last_week',
  'active_last_2_weeks',
  'active_last_4_weeks',
  'no_activity_last_4_weeks',
] as const;

export const activityBucketSchema = z.literal(ACTIVITY_BUCKETS);
export type ActivityBucket = z.infer<typeof activityBucketSchema>;

/**
 * The window width, in whole days, each bucket denotes — a ROLLING window
 * anchored at the instant the request is served, `[now - N days, now]`,
 * never a calendar boundary. `DUE_BUCKET_WINDOW_DAYS`'s own precedent, and
 * for its own stated reason: naming the width here, once, is what lets a
 * test assert the exact boundary a compiled query used rather than hard-code
 * a second copy of it. `apps/server/src/boards/filter.ts`'s
 * `matchesActivityFacet` is the sole reader, and it takes `now` from the
 * SAME `Date` `matchesBoardFilter` already threads through every other
 * facet — never `sql\`now()\`` inside the predicate itself.
 *
 * ⚠⚠ **`no_activity_last_4_weeks` carries 28, IDENTICAL to
 * `active_last_4_weeks`, and that identity is required rather than a
 * coincidence to "fix".** The two buckets are exact complements over the
 * same 28-day window — a card is in exactly one of them, never neither —
 * which is what `FR-SEARCH-006`'s own description of the fourth bucket as
 * an "inverse" of the third demands. A different number (30, say) would
 * open a gap of instants belonging to neither bucket, and a card last
 * touched 29 days ago would vanish from both when a caller filtered by one
 * or the other — a silent hole with a 200 and nothing red anywhere.
 */
export const ACTIVITY_BUCKET_WINDOW_DAYS = {
  active_last_week: 7,
  active_last_2_weeks: 14,
  active_last_4_weeks: 28,
  no_activity_last_4_weeks: 28,
} as const;

/**
 * The column width `board_filter_states.keyword` is declared at —
 * `varchar(255)` (`db/postgres/migrations/0005_views_and_search.sql`) — and
 * therefore the wire's own limit too: a longer value must fail on the
 * FILTER request itself, at `boardFilterKeywordSchema` below, rather than
 * parse cleanly here and fail later on `BE-32.2`'s save with a 500 from the
 * database instead of a 422 from this package.
 */
export const KEYWORD_MAX_LENGTH = 255;

/**
 * The keyword facet's wire type — `FR-SEARCH-007`. One free-text scalar,
 * `boardTitleSchema`'s/`cardTitleSchema`'s own `.trim().min(1).max()` idiom:
 * TRIMMED before either bound is checked, `.min(1)` AFTER trimming, and
 * capped at `KEYWORD_MAX_LENGTH`.
 *
 * ⚠ **`?keyword=` and `?keyword=%20` are both 422s, on the identical rule
 * `members`/`labels`/`due`/`completion`/`activity` already state**: a
 * client meaning "no keyword constraint" omits the key; one that sends
 * whitespace has a bug worth being told about rather than a request
 * silently equivalent to omitting it.
 *
 * This single schema bounds the raw string on BOTH surfaces that carry
 * it — `boardFilterQuerySchema`'s querystring key, below, and
 * `boardFilterStateSchema`'s JSON key, further down — because both persist
 * or filter by the identical raw value: `board_filter_states.keyword`
 * stores this exact trimmed string, not `parseKeywordTerms`'s tokenised
 * output, so the panel redisplays what the user actually typed and
 * re-tokenising on read is free (§3.8 of this block's design notes).
 *
 * Splitting the trimmed string into AND-across-words terms is a DIFFERENT
 * concern with a different owner — `parseKeywordTerms`, below — kept
 * separate so each can be unit-tested against a narrower contract: this
 * schema only bounds length and rejects the all-whitespace case.
 */
export const boardFilterKeywordSchema = z.string().trim().min(1).max(KEYWORD_MAX_LENGTH);

/**
 * Tokenises the keyword facet's raw wire string into the AND-across-words
 * terms `apps/server/src/boards/filter.ts`'s `matchesKeywordFacet` ANDs
 * together, one `ILIKE` per element of the returned array. Pure and
 * database-free, so the whole tokeniser is unit-testable with no container:
 *
 * ⚠⚠ **Each returned term still needs escaping before it reaches an `ILIKE`
 * pattern, and that escaper is NOT declared in this file.**
 * `escapeLikePattern` — `%`, `_` and `\` are `LIKE`/`ILIKE` metacharacters,
 * so a term containing any of them (a card titled "100% off" or
 * "snake_case migration") would otherwise hand the database a wildcard the
 * user never typed, a WRONG result set behind a 200 with nothing red
 * anywhere — **already exists**, at `./search.ts` (B20,
 * `SEARCH_QUERY_MIN_LENGTH`'s neighbour), doing this exact three-character
 * escape for the identical reason. Declaring a second copy here would be
 * the drift this package's own conventions exist to prevent: `./search.ts`'s
 * own docstring is a standing argument against a naive three-sequential-
 * `.replace()` body specifically — "a maintainer 'simplifying' this into
 * three `.replace()` calls reintroduces an ordering requirement that
 * currently does not exist," referring to its own single-pass
 * character-class implementation, which is the more robust of the two
 * shapes regardless of the export collision. `apps/server/src/boards/
 * filter.ts`'s `matchesKeywordFacet` imports `escapeLikePattern` from
 * `@molt/shared` (it resolves to `./search.ts`'s declaration via
 * `export * from './search'` in `./index.ts`) and calls it once per term
 * from this function's output. ⚠ **It does not wrap the value in `%…%`** —
 * the caller does, after escaping, exactly as `./search.ts`'s own docstring
 * argues for its one caller today.
 *
 *   - Splits on whitespace.
 *   - Double quotes GROUP — `"invoice draft"` is one term containing a
 *     space (`FR-SEARCH-007` AC2).
 *   - Empty terms are dropped.
 *   - An unterminated quote closes at end of input rather than swallowing
 *     nothing usefully further — a user who is still typing, not an error.
 *   - Deduplicates, preserving first-seen order — `splitFacet`'s own rule
 *     (`apps/server/src/boards/filter.ts`), kept here for the identical
 *     reason: it keeps a compiled-SQL assertion deterministic regardless of
 *     how many times a term was repeated.
 *   - `[]` is what the arm reads as no keyword constraint at all — not
 *     "matches nothing". ⚠ **Whitespace-only input never actually reaches
 *     this function on the wire**: `boardFilterKeywordSchema`'s `.min(1)`
 *     (after `.trim()`) refuses `?keyword=%20` upstream with a 422, so the
 *     one live path to `[]` through this schema is quotes-only input like
 *     `'""'`, which passes the length check (two characters) and then
 *     tokenises to nothing. Both are tested below; this function accepts
 *     whitespace-only input too (`parseKeywordTerms('   ')` is `[]`), for
 *     the OTHER caller — `apps/web`, which can call this function directly
 *     on a value the user is still typing, before it has been trimmed or
 *     bounds-checked by the schema at all.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ AND-ACROSS-WORDS, NOT OR — THREE DOCUMENTS DISAGREE, AND THIS ONE WINS
 * ON THE RECORD
 * ---------------------------------------------------------------------------
 * `FR-SEARCH-007`'s own Behaviour text (`docs/requirements/
 * 05-search-and-filter.md`) says the OPPOSITE of what this function
 * implements: *"Multiple words are treated as OR — a card matches if it
 * contains any of the entered words."* Two other documents overrule it, and
 * `implementation-plan.md`'s own assignment of this item is what settles it:
 *
 *   - `docs/requirements/00-overview-and-mvp-scope.md:574`, a DECISION
 *     RECORD rather than domain prose, naming this exact requirement:
 *     *"Unify both on AND-across-words. Trello's board filter matches any
 *     entered word while global search requires all of them. … Two
 *     different text semantics in one product is indefensible."*
 *   - `implementation-plan.md`'s `§B32` header repeats it as an instruction
 *     addressed to this block by name: the board filter's text matching is
 *     AND-across-words, and `FR-SEARCH-007` is to be implemented
 *     accordingly.
 *
 * `CLAUDE.md`'s own tie-breaker ("behaviour → the domain document") would
 * pick `05-`'s OR reading, and it does not apply here: that ladder resolves
 * a behaviour question where the domain document is the ONLY one to have
 * spoken. Here `00-` spoke ABOUT this exact sentence, naming it — the
 * decision-record case the ladder defers to, not the case it governs.
 * `FR-SEARCH-007` itself is not silently rewritten; the decision record is
 * recorded here as overruling it, in place, so a later reader does not
 * conclude the ladder was simply ignored.
 *
 * ⚠⚠ **The consequence: `FR-SEARCH-007` AC1 cannot hold, and this tokeniser
 * is why.** The requirement's own acceptance criterion — *"Given cards
 * titled 'Invoice draft' and 'Draft copy', when the user types
 * `invoice draft`, then both cards are visible"* — fails under AND: "Draft
 * copy" contains no "invoice" and is excluded, so only "Invoice draft" is
 * visible. `apps/server/test/unit/FR-SEARCH-007.spec.ts` asserts the
 * CORRECTED outcome and names AC1 as the deliberate deviation, citing this
 * docstring and `00-:574`. AC2 (the quoted-phrase case) is unaffected
 * either way — a single quoted term is not a multi-word AND/OR question at
 * all — which is why asserting AC2 as well as the corrected AC1 still
 * matters even though both now narrow to the same visible card: AC1
 * exercises this function's unquoted TWO-TERM path, AC2 its quoted
 * SINGLE-TERM path, and a spec asserting only one of them stops
 * discriminating the tokeniser.
 */
export function parseKeywordTerms(raw: string): string[] {
  const terms: string[] = [];
  const { length } = raw;
  let i = 0;

  while (i < length) {
    // Skip the run of whitespace separating terms (or leading the string).
    // ⚠ `.charAt(i)` rather than `raw[i]` — always a `string` (empty past
    // the end), never `string | undefined`, so no non-null assertion is
    // needed to satisfy this workspace's type-aware lint.
    while (i < length && /\s/.test(raw.charAt(i))) i++;
    if (i >= length) break;

    if (raw.charAt(i) === '"') {
      // A quoted group: everything up to the next '"', or to end of input
      // if the quote is never closed — "a user still typing", not an error.
      i++; // step past the opening quote
      const start = i;
      while (i < length && raw.charAt(i) !== '"') i++;
      const term = raw.slice(start, i);
      if (i < length) i++; // step past the closing quote, when there is one
      if (term.length > 0) terms.push(term);
      continue;
    }

    // A bare word: everything up to the next whitespace or quote.
    const start = i;
    while (i < length && !/\s/.test(raw.charAt(i)) && raw.charAt(i) !== '"') i++;
    terms.push(raw.slice(start, i));
  }

  // Deduplicate, preserving first-seen order — `splitFacet`'s own rule,
  // for the identical reason: a deterministic compiled-SQL assertion.
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const term of terms) {
    if (!seen.has(term)) {
      seen.add(term);
      deduped.push(term);
    }
  }
  return deduped;
}

/**
 * The querystring of `GET /boards/:id/contents` — B13's route, B19's parameters.
 *
 * ⚠ **`z.strictObject`, and it is doing two jobs at once.** The first is this
 * block's: `?member=<id>` (singular) and `?label=<id>` are 422s rather than
 * whole unfiltered boards, which matters more here than for a typical typo —
 * a filter that silently does nothing looks exactly like a filter that matched
 * everything. The second is **B13's, inherited**: this schema replaces the
 * `z.strictObject({})` that made `?state=archived` a 422, and that guarantee
 * now rides on this declaration. `FR-VIEW-007`'s edge case puts the archived
 * population on a different surface entirely (`FR-EXP-010`, `BE-22.2`), so a
 * client that guesses `?state=archived` must be refused rather than handed the
 * open board face and left to read it as an empty archive.
 *
 * All six facets are `.optional()`, and every combination is legal: none
 * (B13's response, unchanged), one, several, or all six at once. All six
 * absent is not a special case anywhere — it is the shape of every board open
 * that is not filtered.
 *
 * ⚠ **`due` and `completion` are B24's (`BE-24.5`), added beside B19's two
 * without touching either.** Same `z.strictObject`, same `.optional()`
 * pattern, same "the empty string is a 422, the omitted key is no
 * constraint" rule `members`/`labels` already established — a later facet
 * inherits the shape rather than re-deciding it.
 *
 * ⚠ **`activity` and `keyword` are B32's (`BE-32.1`), added beside the other
 * four without touching any of them.** Neither is `boardFilterFacetSchema`
 * and neither carries a `z.preprocess` array pipe — both are SINGLE-VALUED
 * scalars (`ACTIVITY_BUCKETS`'s and `boardFilterKeywordSchema`'s own
 * docstrings, above, argue why), so a REPEATED key
 * (`?activity=A&activity=B`) arrives as an array from Fastify's parser and
 * is refused with a 422, which is correct for a facet this schema cannot
 * express as a selection of several. `apps/server/src/boards/filter.ts`'s
 * `matchesBoardFilter` ANDs all six facets' own arms together, so B19's rule
 * ("OR within a facet, AND across facets") composes over six facets exactly
 * as it did over four, with no seventh rule to state — `FR-SEARCH-008`'s own
 * promise, kept for free a second time.
 */
// ---------------------------------------------------------------------------
// B56 — THE PER-CUSTOM-FIELD FACET (`FR-SEARCH-031`)
// ---------------------------------------------------------------------------
//
// `FR-SEARCH-031`: "The board filter panel offers one facet per custom field
// defined on the board, in the definitions' own order. The operator offered
// depends on the field's type … Selections compose exactly as `FR-SEARCH-008`
// composes every other facet — OR within one field, AND across fields."
//
// ⚠⚠ **SESSION STATE, NEVER STORED STATE — THIS IS THE REQUIREMENT'S OWN
// SETTLED EDGE CASE AND IT IS THE ONE THING HERE THAT FAILS SILENTLY.**
// These criteria live in the querystring and in `FR-SEARCH-009`'s chips for
// the life of the filter, and are **deliberately not** written into a saved
// or shared view. `boardFilterStateSchema`, ~250 lines below, is
// `FR-SEARCH-011`'s persisted filter (`board_filter_states`,
// `apps/server/src/routes/board-filter-state.ts`) and **gains no member for
// this facet**. The physical adjacency of the two schemas in this one file,
// plus the reflex to "keep them in sync", is exactly how that prohibition
// would be violated with nothing red anywhere — so it is stated here, at the
// schema that must not be copied, rather than only in the requirement.
//
// The reason is not squeamishness: storing a criterion needs a durable field
// identity that survives a rename, a retype and a delete, and a durable option
// identity that survives an option being removed. No requirement in
// `05-search-and-filter.md` or `04-views.md` defines either, and inventing one
// to satisfy a facet nothing yet asks to save is the reconstruction
// `docs/requirements/README.md` warns against.
//
// ---------------------------------------------------------------------------
// WHY THE CRITERION CARRIES ITS OWN OPERATOR
// ---------------------------------------------------------------------------
// This schema has no custom field definitions to consult, so it cannot know
// that field `01H…` is a dropdown rather than a text field. A criterion
// spelled `<fieldId>:<value>` would therefore be **ambiguous by
// construction** — a text field holding the literal value `ticked`, or a text
// field whose value happens to be a ULID, would be read as a checkbox state or
// as an option reference. The wire form is `<fieldId>:<kind>:<payload>`
// instead, so every criterion says which operator it is and the server needs
// no definition read to interpret one.
//
// A criterion whose `kind` disagrees with the field's actual type is **not an
// error**: it matches nothing, exactly as this file already promises for "a
// well-formed id that is not on the board". Type-appropriateness is the
// panel's job, and `apps/web/src/lib/board-filter.ts`'s
// `pruneBoardFilterFacets` drops such a criterion the moment the definitions
// are known, which is `FR-SEARCH-031` AC5's own mechanism.

/**
 * The checkbox operator's two states — `FR-SEARCH-031`: "checkbox filters on
 * ticked or unticked".
 *
 * ⚠ **`unticked` is `NOT EXISTS`, and that is a decision, not an oversight.**
 * `custom_field_values.value_checkbox` is only ever written `true` (see
 * `apps/server/src/routes/custom-fields.ts`), so an unticked checkbox has no
 * value row at all. Reading `FR-SEARCH-031`'s general rule — "a field with no
 * value on a card excludes that card from any selection on that field" —
 * literally would therefore make `unticked` **unsatisfiable for every card on
 * every board, forever**, with a 200 and a valid empty body: an explicitly
 * offered operator, dead on arrival. That is the identical failure shape
 * `apps/server/src/boards/filter.ts`'s `matchesMemberFacet` and
 * `matchesDueBucketFacet` docstrings each argue against at length. The plain
 * reading of "ticked or unticked" wins, and the no-value rule governs the four
 * operators that actually read a value.
 */
export const CUSTOM_FIELD_CHECKBOX_STATES = ['ticked', 'unticked'] as const;

export const customFieldCheckboxStateSchema = z.literal(CUSTOM_FIELD_CHECKBOX_STATES);
export type CustomFieldCheckboxState = z.infer<typeof customFieldCheckboxStateSchema>;

/**
 * The date operator's buckets — `FR-SEARCH-031`: "date filters on **the same
 * relative buckets** the due-date facet uses".
 *
 * **Derived from `DUE_BUCKETS`, never restated.** A parallel bucket set is the
 * defect this derivation exists to make impossible: the two would drift the
 * first time one of them gained a window and the other did not, and nothing
 * would be red.
 *
 * ⚠ **`no_due_date` is excluded, and the requirement's own word is why.** It
 * asks for the *relative* buckets; `no_due_date` is not a relative window at
 * all but the due facet's absence sentinel (`isNull(cards.dueAt)`), and
 * offering it here would contradict `FR-SEARCH-031`'s explicit rule that "a
 * field with no value on a card excludes that card from any selection on that
 * field" — the one criterion whose whole meaning is *select the cards with no
 * value*. The four that remain are exactly the windows
 * `DUE_BUCKET_WINDOW_DAYS` sizes, plus `overdue`.
 */
export type CustomFieldDateBucket = Exclude<DueBucket, 'no_due_date'>;

export const CUSTOM_FIELD_DATE_BUCKETS: readonly CustomFieldDateBucket[] =
  DUE_BUCKETS.filter(
    (bucket): bucket is CustomFieldDateBucket => bucket !== 'no_due_date',
  );

const customFieldDateBucketSchema = dueBucketSchema.refine(
  (bucket): bucket is CustomFieldDateBucket => bucket !== 'no_due_date',
);

/**
 * One selected criterion, parsed — the shape both halves of the product build
 * an arm from.
 *
 * Declared as a schema and derived with `z.infer`, this package's standing
 * rule, even though the criterion crosses the wire as a **string**: the parse
 * is what decides whether a string is a criterion at all, so it is the schema
 * that has to be the single producer of the shape.
 */
export const customFieldCriterionSchema = z.discriminatedUnion('kind', [
  z.object({
    fieldId: ulidSchema,
    kind: z.literal('checkbox'),
    state: customFieldCheckboxStateSchema,
  }),
  z.object({ fieldId: ulidSchema, kind: z.literal('option'), optionId: ulidSchema }),
  z.object({
    fieldId: ulidSchema,
    kind: z.literal('date'),
    bucket: customFieldDateBucketSchema,
  }),
  z.object({
    fieldId: ulidSchema,
    kind: z.literal('text'),
    text: z.string().min(1).max(CUSTOM_FIELD_TEXT_VALUE_MAX_LENGTH),
  }),
  z.object({
    fieldId: ulidSchema,
    kind: z.literal('number'),
    value: z.number().finite(),
  }),
]);

export type CustomFieldCriterion = z.infer<typeof customFieldCriterionSchema>;

/**
 * The longest legal criterion string: a ULID, a colon, the longest kind word,
 * a colon, and a text payload at its column width. Stated as arithmetic rather
 * than as a number so it moves when `CUSTOM_FIELD_TEXT_VALUE_MAX_LENGTH` does.
 */
export const CUSTOM_FIELD_CRITERION_MAX_LENGTH =
  26 + 1 + 'checkbox'.length + 1 + CUSTOM_FIELD_TEXT_VALUE_MAX_LENGTH;

/**
 * A criterion → its wire string. The **only** producer of the grammar; the web
 * builds the querystring with it and the server never spells it at all.
 */
export function formatCustomFieldCriterion(criterion: CustomFieldCriterion): string {
  switch (criterion.kind) {
    case 'checkbox':
      return `${criterion.fieldId}:checkbox:${criterion.state}`;
    case 'option':
      return `${criterion.fieldId}:option:${criterion.optionId}`;
    case 'date':
      return `${criterion.fieldId}:date:${criterion.bucket}`;
    case 'text':
      return `${criterion.fieldId}:text:${criterion.text}`;
    case 'number':
      return `${criterion.fieldId}:number:${criterion.value}`;
  }
}

/**
 * A wire string → a criterion, or `null` when it is not one. The **only**
 * consumer of the grammar, and the same function the querystring schema
 * refines with, so "accepted by the route" and "understood by the SQL builder"
 * cannot come apart.
 *
 * ⚠ **The payload is everything after the SECOND colon, unsplit.** A text
 * criterion may legally contain colons ("Blocked: waiting on legal"), so a
 * three-way `split(':')` would truncate it silently — a filter that quietly
 * matched a prefix rather than what the member typed.
 */
export function parseCustomFieldCriterion(raw: string): CustomFieldCriterion | null {
  if (raw.length > CUSTOM_FIELD_CRITERION_MAX_LENGTH) return null;

  const firstColon = raw.indexOf(':');
  if (firstColon < 0) return null;
  const secondColon = raw.indexOf(':', firstColon + 1);
  if (secondColon < 0) return null;

  const fieldId = raw.slice(0, firstColon);
  const kind = raw.slice(firstColon + 1, secondColon);
  const payload = raw.slice(secondColon + 1);

  const candidate =
    kind === 'checkbox'
      ? { fieldId, kind, state: payload }
      : kind === 'option'
        ? { fieldId, kind, optionId: payload }
        : kind === 'date'
          ? { fieldId, kind, bucket: payload }
          : kind === 'text'
            ? { fieldId, kind, text: payload }
            : kind === 'number'
              ? { fieldId, kind, value: Number(payload) }
              : null;

  if (candidate === null) return null;
  /**
   * ⚠ `Number('')` is `0` and `Number(' ')` is `0`, so an empty numeric
   * payload would parse as a criterion for zero. Refused explicitly here
   * rather than left to `z.number()`, which would accept it.
   */
  if (candidate.kind === 'number' && payload.trim() === '') return null;

  const parsed = customFieldCriterionSchema.safeParse(candidate);
  return parsed.success ? parsed.data : null;
}

/**
 * The `customField` facet's selection, normalised to an array — the same
 * `z.preprocess` shape and the same reasoning as `boardFilterFacetSchema`
 * above (Fastify does not wrap a single occurrence), declared separately
 * because this facet's options are neither ULIDs nor a closed enum, which is
 * the property that file header warns must be re-checked rather than assumed.
 */
export const customFieldCriterionQuerySchema = z.preprocess(
  (value: unknown): unknown[] => (Array.isArray(value) ? (value as unknown[]) : [value]),
  z
    .array(
      z
        .string()
        .max(CUSTOM_FIELD_CRITERION_MAX_LENGTH)
        .refine(
          (raw) => parseCustomFieldCriterion(raw) !== null,
          'Expected <fieldId>:<checkbox|option|date|text|number>:<value>',
        ),
    )
    .min(1),
);

export const boardFilterQuerySchema = z.strictObject({
  /**
   * `FR-SEARCH-002` — the member facet. User ULIDs, `'none'` for cards with no
   * member assigned, or both.
   *
   * `'none'` is `card_members`-empty and nothing subtler: a card is unassigned
   * when no row names it, regardless of who created it, who commented on it or
   * who is watching it. `FR-CARD-007`'s join/leave and `FR-CARD-008`'s assign
   * write the only rows this facet reads.
   */
  members: boardFilterFacetSchema
    .optional()
    .describe(
      'Show only cards assigned to any of these users. Repeat the key for ' +
        'several (?members=A&members=B) — they combine with OR, so the visible ' +
        'set grows with each one. The literal “none” selects cards with no ' +
        'member at all and may be combined with ids in the same facet, which ' +
        'yields unassigned cards PLUS those members’ cards. A well-formed id ' +
        'that is not on the board matches nothing and is not an error. Omit ' +
        'the key entirely for no member constraint; ?members= (empty) is a 422.',
    ),
  /**
   * `FR-SEARCH-003` — the label facet. Label ULIDs, `'none'` for cards
   * carrying no label, or both.
   *
   * ⚠ **This selects cards; it never selects which labels a card comes back
   * carrying.** A card matching on red still returns every label it holds —
   * see `apps/server/src/boards/filter.ts`, which argues the failure mode at
   * length, and `FR-CARD-009` AC2, which requires all of a card's labels on
   * its front in the board's set order whether or not a filter is active. The
   * board's top-level `labels` set is likewise never narrowed: it is the facet
   * the panel is drawn from, so filtering it would delete the control the user
   * is using.
   */
  labels: boardFilterFacetSchema
    .optional()
    .describe(
      'Show only cards carrying any of these labels. Repeat the key for ' +
        'several (?labels=A&labels=B) — they combine with OR, and a card ' +
        'carrying both is shown once. The literal “none” selects cards with no ' +
        'label at all and may be combined with ids in the same facet. A ' +
        'well-formed id that is not on the board (a deleted label, say) matches ' +
        'nothing and is not an error. This narrows which CARDS are shown and ' +
        'never which labels a shown card carries.',
    ),
  /**
   * `FR-SEARCH-004` — the due-date bucket facet, `BE-24.5`. One or more of
   * `no_due_date`, `overdue`, `due_next_day`, `due_next_week`,
   * `due_next_month`.
   *
   * The buckets are rolling windows anchored at the instant the request is
   * served, not calendar days, and they OVERLAP: a card due in six hours
   * satisfies `due_next_day`, `due_next_week` AND `due_next_month` all at
   * once, so selecting several is not the same as selecting the widest one
   * alone if the caller's intent was "the union of these named windows" —
   * it already is that union, because each window independently includes it.
   */
  due: dueBucketFacetSchema
    .optional()
    .describe(
      'Show only cards whose due date falls in any of these relative buckets: ' +
        'no_due_date, overdue, due_next_day, due_next_week, due_next_month. ' +
        'Repeat the key for several (?due=overdue&due=due_next_day) — they ' +
        'combine with OR. The three dated buckets are rolling windows starting ' +
        'now and OVERLAP each other (a card due in six hours matches all ' +
        'three); overdue never overlaps any of them. Omit the key entirely ' +
        'for no due-date constraint; ?due= (empty) is a 422 (FR-SEARCH-004).',
    ),
  /**
   * `FR-SEARCH-005` — the completion-status facet, `BE-24.5`. One or both of
   * `marked_complete`, `not_marked_complete`.
   *
   * ⚠ **Independent of `due`.** `overdue` does not itself exclude completed
   * cards — `?due=overdue&completion=marked_complete` is a legal, satisfiable
   * combination (an overdue card marked complete), not a contradiction. A
   * caller wanting "overdue and still outstanding" selects both facets; the
   * due-bucket facet alone never makes that decision on its own account. See
   * `apps/server/src/boards/filter.ts`'s `matchesDueBucketFacet` for why.
   */
  completion: completionFacetSchema
    .optional()
    .describe(
      'Show only cards whose completion state matches: marked_complete, ' +
        'not_marked_complete, or both (which imposes no constraint — ' +
        'FR-SEARCH-005 AC3). Repeat the key to select both. Independent of ' +
        'the due facet: an overdue card marked complete is not excluded by ' +
        '?due=overdue on its own. Omit the key entirely for no completion ' +
        'constraint; ?completion= (empty) is a 422 (FR-SEARCH-005).',
    ),
  /**
   * `FR-SEARCH-006` — the activity-age facet, `BE-32.1`. Exactly ONE of
   * `active_last_week`, `active_last_2_weeks`, `active_last_4_weeks`,
   * `no_activity_last_4_weeks` — see `ACTIVITY_BUCKETS`'s own docstring for
   * why this facet is single-valued where every facet above it is an array.
   *
   * ⚠ **No preprocess pipe wraps this key.** A REPEATED key
   * (`?activity=A&activity=B`) therefore arrives as an array from Fastify's
   * parser and is a 422 — the correct refusal for a facet this schema
   * cannot express as a selection of several, not an oversight relative to
   * `members`/`labels`/`due`/`completion`.
   */
  activity: activityBucketSchema
    .optional()
    .describe(
      'Show only cards whose most recent activity falls in this bucket: ' +
        'active_last_week, active_last_2_weeks, active_last_4_weeks, or ' +
        'no_activity_last_4_weeks (the last two are exact complements over ' +
        'the same 28 days — a card is in exactly one, never neither). ' +
        'SINGLE-VALUED, unlike every other facet on this route: repeating ' +
        'the key (?activity=A&activity=B) is a 422, not a combination. ' +
        'Omit the key entirely for no activity constraint (FR-SEARCH-006).',
    ),
  /**
   * `FR-SEARCH-007` — the keyword-text facet, `BE-32.1`. One free-text
   * scalar, trimmed, at most `KEYWORD_MAX_LENGTH` (255) characters —
   * `board_filter_states.keyword`'s own column width.
   *
   * ⚠ **AND-across-words, not OR — see `parseKeywordTerms`'s docstring for
   * the three-document decision record that settles it.** Double-quote a
   * phrase for an exact match; unquoted words must ALL appear for a card to
   * match. Case-insensitive. A keyword matching no card is a 200 with every
   * list present and empty, never an error.
   *
   * ---------------------------------------------------------------------
   * ⚠⚠ WHAT A TERM IS MATCHED AGAINST — WIDENED TO CUSTOM FIELD VALUES
   * ---------------------------------------------------------------------
   * `FR-SEARCH-007` Behaviour: *"The keyword facet matches against card
   * titles **and custom field values**."* Its own edge case gates that on
   * "the Custom Fields capability being present (see 02-card-detail.md)";
   * **B56 shipped the custom-field value routes, so the gate is now
   * satisfied** and the deferral this docstring used to record ("never
   * custom fields — B56 added the value routes but not the contract") is
   * discharged. `FR-CARD-027` states the same thing from the other side:
   * *"Custom field values are filterable."*
   *
   * A card matches a term if the term appears in **the card title OR in any
   * one of the card's custom-field values**; all terms must still match, and
   * two terms may be satisfied by two different fields. That follows from
   * `FR-SEARCH-008` — OR-within-facet, AND-across-facets — and keyword is
   * ONE facet: requiring every term in the SAME field would make "urgent
   * 2026" unmatchable for a card titled "urgent" holding a field value
   * "2026", which is the commonest real query.
   *
   * **Which value shapes participate**: the text value and the selected
   * dropdown option's label. **Number, date and checkbox values do NOT** —
   * they have no canonical member-visible spelling to agree with
   * (`numeric(20,4)` renders `3` as `'3.0000'`, and a `timestamptz(3)` has
   * no locale), so matching them would make the facet a property of
   * Postgres's cast format rather than of what the member can read. Named as
   * a residual; reopening it needs a rendering contract, not a schema change.
   *
   * **Hidden custom fields still match.** `FR-CARD-027`'s edge case: a
   * hidden field "remains durable data and can still hold values; hidden
   * only suppresses card-back/card-front presentation." Hidden is a
   * presentation setting, not access control, and filtering is not
   * presentation.
   *
   * **Still never** descriptions, comments or checklist items —
   * `FR-SEARCH-007`'s edge case forbids all three, and this widening does
   * not touch them.
   *
   * ⚠ **One divergent producer — and the divergence is now ONE arm, not
   * two.** `apps/server/src/routes/calendar-view.ts`'s
   * `calendarItemKeywordFilter` is a SECOND implementation of this facet,
   * applied to the checklist-item half of the calendar.
   *
   * ⚠⚠ **THIS NOTE USED TO END "…and will not gain the custom-field match",
   * AND THAT CLAUSE IS DEAD — do not restore it.** That producer gained the
   * custom-field arm when the calendar half was brought to parity; measured,
   * the two `EXISTS` expressions are 18 code lines byte-identical once the
   * two table aliases are renamed apart. The surviving half of the old note
   * is the first clause: that producer also matches `checklist_items.text`,
   * which the edge case above forbids **of a card**, because `FR-VIEW-020`
   * makes a dated checklist item a calendar entry in its own right rather
   * than a card matching through one.
   *
   * The card half of every surface — board face, Table, My Cards, calendar —
   * goes through `matchesBoardFilter` and never reaches item text. That
   * asymmetry is the reason the two producers were examined for a collapse
   * and kept separate on merit; the argument in full, and the guard case that
   * fails a naive collapse, live in
   * `apps/server/src/boards/filter.ts`'s `matchesKeywordFacet` docstring and
   * `apps/server/src/routes/calendar-view.integration.test.ts`.
   */
  keyword: boardFilterKeywordSchema
    .optional()
    .describe(
      'Show only cards where every one of the entered words appears in the ' +
        'card TITLE or in one of the card’s CUSTOM FIELD VALUES ' +
        '(AND-across-words — deliberately not the "OR" reading in ' +
        'FR-SEARCH-007’s own Behaviour text; see ' +
        '00-overview-and-mvp-scope.md:574 and parseKeywordTerms’s docstring ' +
        'for the decision record that overrules it). Two words may be ' +
        'satisfied by two different fields. Double-quote a phrase ' +
        '("invoice draft") for an exact-phrase match instead. ' +
        'Case-insensitive. Custom-field text values and the selected ' +
        'dropdown option’s label participate, including on hidden fields; ' +
        'number, date and checkbox values do not (no canonical spelling). ' +
        'Never descriptions, comments or checklist items. Trimmed, 1-255 ' +
        'characters after trimming. Omit the key entirely for no keyword ' +
        'constraint; ?keyword= or an all-whitespace value is a 422.',
    ),
  /**
   * `FR-SEARCH-031` — the per-custom-field facet, `BE-56.5`. Repeatable;
   * each value is one criterion, `<fieldId>:<kind>:<payload>` — see
   * `parseCustomFieldCriterion` above for the grammar and for why the kind is
   * on the wire rather than inferred.
   *
   * ⚠ **One facet on this schema, MANY facets in the panel.** The panel shows
   * one facet per field, and `FR-SEARCH-031` composes them as
   * `FR-SEARCH-008` composes everything else — OR within one field, AND
   * across fields. That grouping is done by
   * `apps/server/src/boards/filter.ts`'s `matchesCustomFieldFacet`, which
   * buckets the criteria by `fieldId` first; it is **not** the
   * one-OR-group-per-key rule the six facets above follow, and reading this
   * key as a single OR group would make "Priority is High AND Size is Large"
   * unexpressible.
   */
  customField: customFieldCriterionQuerySchema
    .optional()
    .describe(
      'Show only cards whose custom field values match these criteria. ' +
        'Repeat the key for several (?customField=A&customField=B). Each ' +
        'value is <fieldId>:<kind>:<payload>, where kind is one of checkbox ' +
        '(ticked|unticked), option (an option ULID), date (overdue| ' +
        'due_next_day|due_next_week|due_next_month — the due facet’s own ' +
        'relative buckets, minus its no_due_date sentinel), text (a ' +
        'case-insensitive containment match) or number (an exact numeric ' +
        'equality). Criteria on the SAME field combine with OR; criteria on ' +
        'DIFFERENT fields combine with AND (FR-SEARCH-008). A card with no ' +
        'value for a field is excluded by every criterion on that field ' +
        'except checkbox:unticked, which is exactly “no ticked value”. A ' +
        'criterion naming a hidden field, a field on another board or a ' +
        'field that no longer exists imposes NO constraint and is not an ' +
        'error (FR-SEARCH-031 AC5). Omit the key entirely for no ' +
        'custom-field constraint; ?customField= (empty) is a 422.',
    ),
});

/**
 * The parsed filter as the route receives it — `request.query`.
 *
 * ⚠ Derived with `z.infer` and never declared beside the schema, this
 * package's standing rule. It is the **output** type, so `members`/`labels`
 * are `('none' | string)[] | undefined`, `due`/`completion` are their own
 * closed-enum arrays, and `activity`/`keyword` are `ActivityBucket |
 * undefined` and `string | undefined` respectively — SCALARS, never arrays,
 * which is `ACTIVITY_BUCKETS`'s and `boardFilterKeywordSchema`'s own point
 * and not an inconsistency with the four array facets above. The four array
 * fields carry no ambiguity about which of Fastify's wire shapes produced
 * them; a client building a query string writes the array form and
 * Fastify's parser accepts either. `activity`/`keyword` have no such
 * ambiguity to resolve — Fastify hands back a bare string or nothing, never
 * an array, unless the caller repeats the key, and a repeated key is
 * refused before it ever reaches this type.
 */
export type BoardFilterQuery = z.infer<typeof boardFilterQuerySchema>;

/**
 * The member facet's "the caller" option — `?members=me` on
 * `GET /boards/:id/contents` ONLY.
 *
 * A connected-app bearer (B82) has no route that tells it its own user id, so
 * an AI account that wants "only my cards on this board" cannot spell the
 * member id the facet otherwise requires. The server resolves the literal to
 * the authenticated caller's id before the filter is built.
 *
 * Cannot collide with a ULID for the same reason `NO_FACET_MATCH` cannot: a
 * ULID is 26 characters of Crockford base 32 (`ulidSchema`) and `'me'` is two.
 * It is matched **exactly and in lowercase** — `ME` is a 422, as `NONE` is —
 * so the wire has one spelling.
 *
 * ⚠ **It is accepted by `boardContentsQuerySchema` and by NO other schema in
 * this package.** `boardFilterQuerySchema` still refuses it, because eight
 * routes parse that schema and the other seven (the views, the persisted
 * filter state, the dashboard) would each have to resolve it or silently treat
 * it as an id that matches nothing. Widening is a decision per route.
 */
export const CALLER_FACET_MATCH = 'me';

/**
 * The contents route's member option: `'none'`, `'me'` or a ULID.
 *
 * ⚠ **ULID stays last** for the reason `boardFilterOptionSchema` documents:
 * Zod reports the last branch's issue for a value matching none, so
 * `?members=xyz` still reports the ULID format failure.
 */
const boardContentsMemberOptionSchema = z.union([
  z.literal(NO_FACET_MATCH),
  z.literal(CALLER_FACET_MATCH),
  ulidSchema,
]);

/**
 * `GET /boards/:id/contents`'s querystring — **a copy of
 * `boardFilterQuerySchema` whose `members` facet also accepts `'me'`**, and
 * nothing else different. Still strict.
 *
 * ⚠ **A copy, not a widening in place**: one schema serialises eight routes
 * and widening it changes what seven of them accept. `.extend` replaces only
 * the one key and keeps the object strict (pinned by a unit test).
 */
export const boardContentsQuerySchema = boardFilterQuerySchema.extend({
  members: z
    .preprocess(
      (value: unknown): unknown[] =>
        Array.isArray(value) ? (value as unknown[]) : [value],
      z.array(boardContentsMemberOptionSchema).min(1),
    )
    .optional()
    .describe(
      'Show only cards assigned to any of these users. Repeat the key for ' +
        'several (?members=A&members=B) — they combine with OR. The literal ' +
        '“none” selects cards with no member at all; the literal “me” selects ' +
        'the authenticated caller’s own cards (an anonymous caller has no ' +
        'cards, so “me” alone matches nothing). Both may be combined with ids ' +
        'in the same facet. A well-formed id that is not on the board matches ' +
        'nothing and is not an error. Omit the key entirely for no member ' +
        'constraint; ?members= (empty) is a 422.',
    ),
});

/** `request.query` on the contents route — `members` may carry `'me'`. */
export type BoardContentsQuery = z.infer<typeof boardContentsQuerySchema>;

// ---------------------------------------------------------------------------
// B32 — THE PERSISTED FILTER STATE (`BE-32.2`)
// ---------------------------------------------------------------------------
//
// `FR-SEARCH-011` — persist a board's filter across sessions. This is the
// JSON half of that contract: `PUT /boards/:id/filter`'s request body and
// both `GET`/`PUT /boards/:id/filter`'s response
// (`apps/server/src/routes/board-filter-state.ts`).

/**
 * The persisted filter, on the wire — `PUT /boards/:id/filter`'s body and
 * both `GET`/`PUT /boards/:id/filter`'s response (`BE-32.2`,
 * `FR-SEARCH-011`).
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ A SIBLING SCHEMA, NOT A REUSE OF `boardFilterQuerySchema` — AND NOT A
 * WIDENING OF IT EITHER
 * ---------------------------------------------------------------------------
 * `boardFilterQuerySchema`'s array facets are `z.preprocess` PIPES that
 * accept a bare string, because Fastify's querystring parser hands back a
 * bare string for a single occurrence of a repeated key
 * (`boardFilterFacetSchema`'s own docstring, above, measures all four
 * shapes). A JSON body is not a querystring: `JSON.parse` already produces a
 * real array or it does not, and reusing the preprocess pipe here would get
 * two things wrong at once — `/docs` would describe a JSON body that also
 * accepts `{"members": "A"}` (a bare string) alongside
 * `{"members": ["A"]}`, documenting a wire shape only Fastify's own query
 * parser ever actually produces, and nothing about a JSON PUT body needs the
 * single-value convenience the pipe exists for in the first place.
 *
 * The package's standing rule ("extend a shipped schema on a copy, never
 * widen it in place") is honoured by this being a SIBLING declared beside
 * `boardFilterQuerySchema`, not a copy of a schema that also serialises a
 * DIFFERENT route's response — `boardContentsSchema` in `./board-contents`
 * still gains nothing from either schema (this file's header, point 3,
 * still holds for it). The element-level vocabularies
 * (`boardFilterOptionSchema`'s sentinel-or-ULID union, `dueBucketSchema`,
 * `completionStateSchema`, `activityBucketSchema`, `boardFilterKeywordSchema`)
 * ARE shared with the querystring schema, because those are the actual
 * facet vocabularies and a second copy of a CHECK-constraint-derived
 * spelling is exactly the drift this package exists to prevent. Only the
 * OUTER shape — plain `z.array`/scalar here, a preprocess pipe there —
 * differs between the two schemas.
 *
 * Six keys, mirroring the six facets `boardFilterQuerySchema` accepts, each
 * stripped of its preprocess pipe:
 *
 *   - `members`, `labels` — plain `z.array(boardFilterOptionSchema)`, no
 *     `.min(1)`: an EMPTY array is this schema's own "nothing selected",
 *     not the unreachable-through-Fastify branch it is on the querystring
 *     (that schema's own comment) — a stored filter can genuinely have zero
 *     members or labels selected, and the empty array is how it says so.
 *   - `due`, `completion` — plain `z.array(dueBucketSchema)` /
 *     `z.array(completionStateSchema)`, the identical reasoning.
 *   - `activity` — `activityBucketSchema.optional()`, a scalar exactly as
 *     the querystring declares it: `ACTIVITY_BUCKETS`'s argument (one
 *     `varchar` column under one CHECK) transfers unchanged regardless of
 *     which transport carries the value.
 *   - `keyword` — `boardFilterKeywordSchema.optional()`, a scalar, ditto.
 *
 * ⚠⚠ **The two schemas can drift — a seventh facet added to one and
 * forgotten on the other — and nothing at the type level stops that, so a
 * test does.** `board-filter.test.ts`'s drift-guard test compares
 * `Object.keys(boardFilterQuerySchema.shape)` against
 * `Object.keys(boardFilterStateSchema.shape)`, so a facet declared on one
 * schema and not the other is a RED TEST rather than a filter that narrows
 * the board and can never be persisted, or a stored field the panel can
 * never actually send.
 *
 * `apps/server/src/routes/board-filter-state.ts` is where this schema
 * actually meets a database row — `toStoredFilter`/`fromStoredFilter` map
 * between this shape and `board_filter_states` plus its two child tables,
 * including the one lossy direction (`completion`'s two-selected-collapses-
 * to-`'any'` case; see that file's own docstring for the argument). This
 * file declares the JSON shape only: it opens no transaction and writes no
 * row.
 */
export const boardFilterStateSchema = z.strictObject({
  members: z.array(boardFilterOptionSchema),
  labels: z.array(boardFilterOptionSchema),
  due: z.array(dueBucketSchema),
  completion: z.array(completionStateSchema),
  activity: activityBucketSchema.optional(),
  keyword: boardFilterKeywordSchema.optional(),
});

/**
 * The persisted filter as a route handler works with it — `z.infer`, this
 * package's standing rule, never declared by hand beside the schema.
 */
export type BoardFilterState = z.infer<typeof boardFilterStateSchema>;
